import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';

import { productTypes } from '../../../types/products';

import { TranslatedCopy } from '../../../intl/TranslatedCopy';

import { H2, Currency, ProductGenderDivision, SpacerDot, Table } from '../../../components';

import { ProductImage } from '../../../components/aurora';

import { hasValueAndIsPositive, maxLength10, handleValidatePrePack } from '~APP_UTIL/formValidations';
import { lessThanAvailable } from '../orderReturnsValidations';

import { createProductName } from '../../../util/createProductName';
import { formatColorString } from '../../../util/formatColorString';

import { getIsGreaterChinaSalesOrg } from '../../../gamedayAccount/selectors';

import getSizeChartTableColumns from '../../getSizeChartTableColumns';

const timerIds = {};

export const OrderReturnsPageProduct = ({
  product,
  returnableData,
  embedded,
  displayOnly,
  submittedData,
  onFormValueUpdate,
  overrideTotalQty,
  overrideTotalAmt,
  currencyCode,
  isGreaterChinaSalesOrg,
  isSelectAllProducts,
  submittableReturnsList,
  setHasQuantityError,
  setIsSelectAllProducts
}) => {
  const {
    id,
    copy: { name },
    productCode,
    division,
    dimensions,
    styleGenderAge,
    colorName,
    retailAmount,
    wholesaleAmount,
    totalShippedQty,
    totalNet
  } = product;

  const { sizes } = returnableData;

  const [sizeData, setSizeData] = useState([]);
  const [sizeGridHasErrors, setSizeGridHasErrors] = useState(false);
  const [calculatedAmount, setCalculatedAmount] = useState(0);

  const handleCellChange = async (sizeCode, value) => {
    const size = sizes.find(size => size.code === sizeCode);
    const sizeIndex = sizes.findIndex(size => size.code === sizeCode);
    const hasError = value > size.returnableQuantity && value > 0;

    const sizeUpdated = { ...size, quantity: Number(value), hasError };
    const sizeDataCopy = [...sizeData];

    sizeDataCopy[sizeIndex] = sizeUpdated;

    setSizeData(sizeDataCopy);

    if (hasError) {
      setSizeGridHasErrors(true);
      setHasQuantityError(true);
    } else {
      setSizeGridHasErrors(false);
    }

    setIsSelectAllProducts(false);

    clearTimeout(timerIds[sizeCode]);

    timerIds[sizeCode] = setTimeout(() => {
      onFormValueUpdate(value, productCode, sizeCode, hasError);
    }, 1000);
  };

  const validatePrePack = handleValidatePrePack(product);
  const validations = [hasValueAndIsPositive, maxLength10, lessThanAvailable];
  if (validatePrePack) validations.push(validatePrePack);

  const hasPreviousReturnQuantity = sizeData.some(size => size.previouslyReturnedQuantity > 0);

  const columns = useMemo(() => getSizeChartTableColumns(id, hasPreviousReturnQuantity, displayOnly), [
    id,
    hasPreviousReturnQuantity
  ]);

  useEffect(() => {
    if (isSelectAllProducts) {
      setSizeData(sizes.map(size => ({ ...size, quantity: size.returnableQuantity, error: null })));
    }
  }, [isSelectAllProducts]);

  useEffect(() => {
    setSizeData(
      sizes.map(size => ({
        ...size,
        quantity: submittableReturnsList?.find(
          submittableReturn => submittableReturn.productCode === productCode && submittableReturn.sizeCode === size.code
        )?.quantity,
        error: null
      }))
    );
  }, [JSON.stringify(submittableReturnsList)]);

  useEffect(() => {
    const formQuantityCheck = sizeData.reduce((acc, size) => acc + (size.quantity || 0), 0);
    const enteredQuantity =
      submittedData && submittedData.productToReturn && submittedData.productToReturn.length > 0
        ? submittedData.productToReturn.filter(prod => prod.productCode === productCode)[0].quantity
        : formQuantityCheck;
    const productNetAmount = (totalNet / totalShippedQty) * (overrideTotalQty || enteredQuantity);
    const calcAmount = wholesaleAmount * enteredQuantity || productNetAmount;
    setCalculatedAmount(calcAmount);
  }, [JSON.stringify(submittedData), sizeData, JSON.stringify(product)]);

  return (
    <div
      className={classnames('orderReturns__select_product column flx-jc-sm-fs bg-white u-full-width', {
        'mb3-sm': !embedded,
        'orderReturns__select_productEmbedded prl7-sm prl12-md pt7-sm pb7-sm': embedded
      })}
      style={{ overflow: 'scroll' }}
    >
      <div
        className={classnames('u-full-width flex-container row start', {
          'p5-sm p8-md prl10-md': !embedded
        })}
      >
        <div className='orderReturns__select_product_img p3-sm border'>
          <ProductImage alt={productCode} productCode={productCode} />
        </div>
        <div
          className={classnames(
            'orderReturns__select_product_info',
            'ml5-sm flex-container column start space-between u-full-width'
          )}
        >
          <div className='pr0-sm pr5-lg'>
            <H2>
              {createProductName({
                name,
                dimensions,
                division
              })}
            </H2>
            <ul className='body-4 mt2-sm pr1-sm text-color-warm-grey'>
              <li>
                <p>
                  <ProductGenderDivision gender={styleGenderAge} division={division} />
                </p>
              </li>
              <li>
                <p>
                  <TranslatedCopy id='global.product.style' />
                  {`: ${productCode}`}
                </p>
              </li>
              <li>
                <p>
                  <TranslatedCopy id='global.product.color' />
                  {`: ${formatColorString(colorName)}`}
                </p>
              </li>
              <li>
                <p>
                  {!isGreaterChinaSalesOrg && (
                    <>
                      <TranslatedCopy id='global.product.retail' />{' '}
                      <Currency value={retailAmount || '0'} type={currencyCode} />
                      <SpacerDot />
                    </>
                  )}
                  {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                  <TranslatedCopy id='global.product.wholesale.net' />{' '}
                  <Currency value={wholesaleAmount || '0'} type={currencyCode} />
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className='pt8-sm mt3-sm mt0-lg body-4 text-color-warm-grey flex-container start'>
        {displayOnly ? (
          <div className='text-color-primary-dark ta-sm-r'>
            <Table
              id={id}
              overrideClass={classnames('cart__table ', 'orderReturns__select_productTotals')}
              data={sizeData}
              columns={columns}
              cellProps={{
                onChange: () => {}
              }}
            />
            <H2 className='pr4-sm pt2-sm'>
              <TranslatedCopy id='orderreturns.netAmount' />
              {': '}
              <Currency value={overrideTotalAmt || calculatedAmount || '0'} type={currencyCode} />
            </H2>
          </div>
        ) : (
          <div className='pl10-sm ta-sm-r'>
            <Table
              id={id}
              overrideClass={classnames('cart__table', 'orderReturns__select_productTotals')}
              data={sizeData}
              columns={columns}
              cellProps={{
                onChange: handleCellChange
              }}
            />
            <p className='pb4-sm pr4-sm'>
              <TranslatedCopy id='orderreturns.netAmount' />
              {': '}
              <Currency value={sizeGridHasErrors ? '0' : calculatedAmount || '0'} type={currencyCode} />
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

OrderReturnsPageProduct.propTypes = {
  product: productTypes.isRequired,
  returnableData: PropTypes.object,
  embedded: PropTypes.bool,
  displayOnly: PropTypes.bool,
  submittedData: PropTypes.object,
  onFormValueUpdate: PropTypes.func,
  overrideTotalQty: PropTypes.number,
  overrideTotalAmt: PropTypes.number,
  currencyCode: PropTypes.string,
  isGreaterChinaSalesOrg: PropTypes.bool,
  isSelectAllProducts: PropTypes.bool,
  submittableReturnsList: PropTypes.array.isRequired,
  setHasQuantityError: PropTypes.func.isRequired,
  setIsSelectAllProducts: PropTypes.func.isRequired
};

OrderReturnsPageProduct.defaultProps = {
  returnableData: null,
  embedded: false,
  displayOnly: false,
  submittedData: null,
  onFormValueUpdate: null,
  overrideTotalQty: null,
  overrideTotalAmt: null,
  currencyCode: null,
  isGreaterChinaSalesOrg: false,
  isSelectAllProducts: false
};

const mapStateToProps = state => ({
  isGreaterChinaSalesOrg: getIsGreaterChinaSalesOrg(state)
});

export default connect(mapStateToProps, {})(OrderReturnsPageProduct);
